import type { OAuthProvider } from "@/features/auth/types/auth-flow.type";

const STORAGE_KEY = "foodango.auth.ssoAttempt";

type SsoAttempt = {
  provider: OAuthProvider;
  /** Known only when the user typed their e-mail before picking a provider. */
  email: string | null;
};

/**
 * The OAuth round trip leaves the app, so the provider we started with has to
 * survive a full page load. `sessionStorage` keeps it out of the URL, where an
 * e-mail address has no business being.
 */
export const saveSsoAttempt = (attempt: SsoAttempt) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempt));
  } catch {
    // Private browsing modes can reject writes; the callback then falls back
    // to a generic error instead of the "account not linked" copy.
  }
};

export const readSsoAttempt = (): SsoAttempt | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<SsoAttempt>;
    if (parsed.provider !== "google" && parsed.provider !== "apple") {
      return null;
    }

    return { provider: parsed.provider, email: parsed.email ?? null };
  } catch {
    return null;
  }
};

export const clearSsoAttempt = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};
